import { difference } from 'lodash';
import { useMemo } from 'react';

import { useApplicationConfig } from '~/data/applicationConfig';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { employeeModulesAccessRoles } from '~/scenes/PortalViews/Views/EmployeeViews/data';

export type ContactTabSectionAccess = {
  canView: boolean;
  canEdit: boolean;
};

type ContactTabAccess = {
  homeContact: ContactTabSectionAccess;
  workContact: ContactTabSectionAccess;
  emergencyContact: ContactTabSectionAccess;
  otherContact: ContactTabSectionAccess;
};

export const useContactTab = () => {
  const { applicationConfig } = useApplicationConfig();

  const {
    isSelectedEmployeeLoggedInUser,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee, isMyselfWithRole },
  } = useSelectedEmployeeId();

  return useMemo((): ContactTabAccess => {
    if (isSelectedEmployeeLoggedInUser) {
      const canEditMyselfRoles = difference(
        employeeModulesAccessRoles.people.full,
        [Roles.DEPUTY_MANAGER],
      );

      return {
        workContact: {
          canView: true,
          canEdit: isMyselfWithRole(canEditMyselfRoles),
        },
        homeContact: {
          canView: true,
          canEdit: true,
        },
        emergencyContact: {
          canView: true,
          canEdit: true,
        },
        otherContact: {
          canView: true,
          canEdit: true,
        },
      };
    }

    const hasAccessWithRoleExcept = (exceptWithRoles: Roles[]) =>
      hasRoleAccessOverEmployee(
        difference(employeeModulesAccessRoles.people.all, exceptWithRoles),
      );

    const sectionAccessRestrictions = (
      cannotView?: Roles[],
      cannotEdit?: Roles[],
    ): ContactTabSectionAccess => {
      return {
        canView: cannotView?.length
          ? hasAccessWithRoleExcept(cannotView)
          : true,
        canEdit:
          cannotView?.length || cannotEdit?.length
            ? hasAccessWithRoleExcept([
                ...(cannotView ?? []),
                ...(cannotEdit ?? []),
              ])
            : true,
      };
    };

    const allManagerRoles = [
      Roles.MANAGER,
      Roles.ALT_MANAGER,
      Roles.DEPUTY_MANAGER,
    ];

    const managerHomeContactAccessEnabled =
      applicationConfig.fComConfig.mgrConfig.fComEnableMgrHomeContact;

    const homeContactHiddenFromRoles = managerHomeContactAccessEnabled
      ? [Roles.ALT_MANAGER]
      : allManagerRoles;

    return {
      workContact: sectionAccessRestrictions(undefined, [Roles.DEPUTY_MANAGER]),
      homeContact: sectionAccessRestrictions(homeContactHiddenFromRoles, [
        Roles.IT_ADMIN,
        Roles.DEPUTY_MANAGER,
      ]),
      emergencyContact: sectionAccessRestrictions(
        [Roles.IT_ADMIN],
        allManagerRoles,
      ),
      otherContact: sectionAccessRestrictions([Roles.IT_ADMIN]),
    };
  }, [
    isSelectedEmployeeLoggedInUser,
    isMyselfWithRole,
    hasRoleAccessOverEmployee,
    applicationConfig,
  ]);
};
